import React from "react";
import { useAppDispatch, useAppSelector } from "../../../../store/hooks";
import { selectors } from "../../../../store/selectors/shipBuilderSelectors";
import { Sensors, SensorType } from "../../../../types/sensors";
import { SENSORS, calculateSensorCost, calculateSensorTons } from "../../../../constants/sensors";
import { setSensors } from "../../../../store/features/shipBuilderSlice";
import { formatCurrency, formatNumber } from "../../../../utils/formatting";
// Removed unused imports - now using direct scaling calculations
import { BreakawaySensorsConfig } from "./BreakawaySensorsConfig";
import { selectIsBreakaway } from "../../../../store/selectors/breakawayHullSelectors";

/**
 * Component for selecting ship sensor suites
 * Allows selection of sensor types and displays their capabilities
 *
 * @component
 * @example
 * ```tsx
 * <SensorsSelector />
 * ```
 */
export const SensorsSelector: React.FC = () => {
  const dispatch = useAppDispatch();
  const sensors = useAppSelector(selectors.selectSensors);
  const shipSize = useAppSelector(selectors.selectShipSize);
  const shipTechLevel = useAppSelector((state) => state.shipBuilder.techLevel);
  const tlOverrideActive = useAppSelector(selectors.selectTLOverrideActive);
  const isBreakaway = useAppSelector(selectIsBreakaway);
  const breakawayHull = useAppSelector((state) => state.breakawayHull);

  // Check if breakaway hulls are configured
  const hasBreakawayHulls = isBreakaway || breakawayHull?.isBreakaway;

  const getBreakawayInfo = () => {
    if (!breakawayHull?.sections) return "";
    const sections = breakawayHull.sections;
    return `${sections.length} section${sections.length > 1 ? 's' : ''} (${sections.map(s => `${s.name}: ${s.size} tons`).join(", ")})`;
  };

  // Convert SENSOR_TYPES Record to array for filtering
  const sensorTypesArray = Object.values(SENSORS).filter(
    (sensor) => tlOverrideActive || sensor.techLevel <= shipTechLevel
  );

  const handleSensorChange = (type: SensorType) => {
    const sensorConfig = SENSORS[type];

    // Use the proper calculation functions that handle scaling correctly
    const scaledCost = calculateSensorCost(type, shipSize);
    const scaledTons = calculateSensorTons(type, shipSize);

    console.log("🔍 SensorsSelector - handleSensorChange DEBUG:");
    console.log("  - Sensor Type:", type);
    console.log("  - Ship Size:", shipSize);
    console.log("  - Sensor Config from SENSORS:", sensorConfig);
    console.log("  - Raw sensor cost from config:", sensorConfig.cost);
    console.log("  - calculateSensorCost result:", scaledCost);
    console.log("  - calculateSensorTons result:", scaledTons);

    const sensor: Sensors = {
      type,
      name: sensorConfig.name || "Unknown Sensors",
      description: sensorConfig.description || "No description available",
      capabilities: sensorConfig.capabilities ? [...sensorConfig.capabilities] : [],
      dm: sensorConfig.dm || 0,
      power: sensorConfig.power || 0,
      tons: scaledTons,
      cost: scaledCost,
      techLevel: sensorConfig.techLevel || 8,
      rating: 1, // Default rating for basic sensors
    };

    console.log("  - Final sensor object being dispatched:", sensor);
    console.log("  - Sensor cost in final object:", sensor.cost);

    dispatch(setSensors(sensor));
  };

  return (
    <div className="space-y-4 pb-24">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
          Sensor Systems Configuration
        </h2>
        <p className="text-gray-600 dark:text-gray-300">
          Select sensor suites for your ship
        </p>
      </div>

      {/* Breakaway Hulls Detection */}
      {hasBreakawayHulls && (
        <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-lg p-4 mb-6">
          <div>
            <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-200 mb-2">
              Breakaway Hulls Detected
            </h3>
            <p className="text-sm text-blue-800 dark:text-blue-300 mb-2">
              {getBreakawayInfo()}
            </p>
          </div>
        </div>
      )}

      {/* Breakaway Sensors Configuration - Inline */}
      {hasBreakawayHulls && breakawayHull?.sections && breakawayHull.sections.length > 0 && (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6 mb-6">
          <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-gray-100">Breakaway Section Sensor Configuration</h3>
          <BreakawaySensorsConfig
            isOpen={true}
            onClose={() => {}} // No-op since we're showing inline
            inline={true}
          />
        </div>
      )}

      {/* Standard Sensor Selection - Hidden when breakaway hulls are enabled */}
      {!hasBreakawayHulls && (
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          Standard Sensor Configuration
        </h3>
        
        <div className="flex flex-col space-y-2">
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Sensor Type</label>
          <select
            value={sensors?.type || "BASIC"}
            onChange={(event) =>
              handleSensorChange(event.target.value as SensorType)
            }
            className="rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100"
          >
            {sensorTypesArray.map((spec) => (
              <option
                key={spec.type}
                value={spec.type}
                disabled={spec.techLevel > shipTechLevel}
                title={
                  spec.techLevel > shipTechLevel
                    ? `Requires TL ${spec.techLevel}`
                    : `Min TL ${spec.techLevel}`
                }
              >
                {spec.name} (Min TL {spec.techLevel})
              </option>
            ))}
          </select>
        </div>

        {sensors && (
          <div className="space-y-4 mt-6">
            <div className="bg-white dark:bg-gray-700 rounded-lg p-4 shadow">
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                Selected Sensors
              </h3>
              <div className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
                <p>Type: {sensors.name}</p>
                <p>
                  DM: {sensors.dm > 0 ? "+" : ""}
                  {sensors.dm}
                </p>
                <p>Power: {formatNumber(sensors.power)} Power</p>
                <p>Weight: {formatNumber(sensors.tons)} tons</p>
                <p>Cost: {formatCurrency(sensors.cost)}</p>
                <p>Tech Level: {sensors.techLevel}</p>
              </div>
            </div>
          </div>
        )}
      </div>
      )}

      {/* Sticky Footer */}
      {!hasBreakawayHulls && sensors && (
        <div className="fixed bottom-0 left-0 right-0 bg-gray-800 text-white p-4 shadow-lg z-10">
          <div className="container mx-auto grid grid-cols-4 gap-4 items-center text-center">
            <div>
              <span className="text-gray-400 block text-sm">Power</span>
              <span className="text-xl font-bold text-yellow-400">
                {formatNumber(sensors.power)} Power
              </span>
            </div>
            <div>
              <span className="text-gray-400 block text-sm">Weight</span>
              <span className="text-xl font-bold text-blue-400">
                {formatNumber(sensors.tons)} tons
              </span>
            </div>
            <div>
              <span className="text-gray-400 block text-sm">Cost</span>
              <span className="text-xl font-bold text-green-400">
                {formatCurrency(sensors.cost)}
              </span>
            </div>
            <div>
              <span className="text-gray-400 block text-sm">DM</span>
              <span className="text-xl font-bold text-purple-400">
                {sensors.dm > 0 ? "+" : ""}
                {sensors.dm}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Breakaway Hulls Message - Show when breakaway hulls are enabled but no sections configured */}
      {hasBreakawayHulls && !breakawayHull?.sections?.length && (
        <div className="mb-6 p-4 border-2 border-yellow-300 dark:border-yellow-600 rounded-lg bg-yellow-50 dark:bg-yellow-900/30">
          <h3 className="text-lg font-semibold text-yellow-800 dark:text-yellow-200 mb-2">
            Breakaway Hulls Detected
          </h3>
          <p className="text-sm text-yellow-700 dark:text-yellow-300">
            Breakaway hulls are enabled but sections are not yet configured. 
            Please configure breakaway sections in the Hull step before configuring sensors.
          </p>
        </div>
      )}
    </div>
  );
};
